import { ChangeDetectionStrategy, Component, inject } from "@angular/core";
import { MatDialogModule, MatDialogRef } from "@angular/material/dialog";
import { TranslateModule } from "@ngx-translate/core";

export type SessionConflictResult = "restore" | "logout";

@Component({
    selector: "next-session-conflict-dialog",
    imports: [MatDialogModule, TranslateModule],
    template: `
        <div class="dialog-icon" aria-hidden="true">
            <span class="material-symbols-rounded">devices</span>
        </div>
        <h2 mat-dialog-title>{{ "realtime.sessionConflict.title" | translate }}</h2>
        <mat-dialog-content>
            <p>{{ "realtime.sessionConflict.message" | translate }}</p>
            <p class="detail">{{ "realtime.sessionConflict.detail" | translate }}</p>
        </mat-dialog-content>
        <mat-dialog-actions>
            <button class="button restore-session" type="button" (click)="close('restore')">
                {{ "realtime.sessionConflict.restore" | translate }}
            </button>
            <button class="logout" type="button" (click)="close('logout')">
                {{ "realtime.sessionConflict.logout" | translate }}
            </button>
        </mat-dialog-actions>
    `,
    styles: [`
        :host { display: block; width: min(460px, calc(100vw - 32px)); padding: 30px; color: var(--theme-text); background: var(--theme-surface); }
        .dialog-icon { display: grid; place-items: center; width: 48px; height: 48px; margin-bottom: 18px; border-radius: 50%; color: var(--theme-primary); background: var(--theme-primary-subtle); }
        .dialog-icon span { font-size: 25px; }
        h2[mat-dialog-title] { margin: 0 0 10px; padding: 0; color: var(--theme-text-strong); font-size: 22px; line-height: 1.3; }
        mat-dialog-content { max-height: none; margin: 0; padding: 0; color: var(--theme-text-body); font-size: 14px; line-height: 1.55; }
        mat-dialog-content p { margin: 0; }
        .detail { margin-top: 8px !important; color: var(--theme-text-muted); }
        mat-dialog-actions { display: grid; gap: 12px; min-height: 0; margin-top: 26px; padding: 0; }
        .button { min-height: 44px; border: 0; border-radius: 7px; padding: 0 18px; color: var(--theme-on-primary); background: var(--theme-primary); box-shadow: 0 6px 16px var(--theme-primary-shadow); cursor: pointer; font: inherit; font-size: 14px; font-weight: 700; }
        .button:hover { background: var(--theme-primary-hover); }
        .logout { justify-self: center; border: 0; padding: 5px 12px; color: var(--theme-text-body); background: transparent; cursor: pointer; font: inherit; font-size: 13px; font-weight: 600; }
        .logout:hover { color: var(--theme-danger-text); }
        @media (max-width: 480px) { :host { padding: 24px; } }
    `],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SessionConflictDialogComponent {
    private readonly dialogRef = inject(MatDialogRef<SessionConflictDialogComponent, SessionConflictResult>);

    close(result: SessionConflictResult): void {
        this.dialogRef.close(result);
    }
}
